import { Vehicle } from '../../../../types/vehicle';
import NumberFormat from 'react-number-format';
import { useAdminOfferAuction, useCustomerHoverStateHandlers } from '../hooks';
import { useOfferAuctionBidsInfo, useIsTextOverflown } from '../../../../hooks';
import styled from 'styled-components';
import { ButtonBase, Icon, Popover } from '@material-ui/core';
import { useRef, useState } from 'react';
import { ViewAllBidsTooltip } from '../../../Shared/VehicleDetails/components/BidHistorySection/components/ViewAllBidsModal';
import { convertToLocalTime } from '../../../../utils';
import { User } from 'types/user';
import { AUCTION_TYPES } from 'constants';
import { VEHICLE_STATUSES } from 'constants/vehicleStatuses';
import { HoverInfoTooltip } from './HoverInfo';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;
  font-weight: 500;
`;

const StyledBackButton = styled(ButtonBase)`
  margin-top: auto;
`;

const WinnerHighlight = styled.span`
  background: rgba(253, 183, 20, 0.3);
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 500;
`;

const WinnerName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
  display: inline-block;
  vertical-align: bottom;
  cursor: pointer;
`;

const WinnerBuyerName = ({ buyerName, userId, auctionId }: { buyerName: string; userId: number; auctionId: number }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isOverflown = useIsTextOverflown(ref);
  const { isOpen, handleClose, handleOpen } = useCustomerHoverStateHandlers();

  return (
    <WinnerHighlight>
      <HoverInfoTooltip
        anchorEl={ref.current}
        initialPage={`/admin/auctions/${auctionId}`}
        userId={userId}
        isPopoverOpened={isOpen}
        closeTooltip={handleClose}
        showTooltip={isOverflown}
        tooltipName={buyerName}
      >
        <WinnerName ref={ref} onClick={handleOpen}>
          {buyerName}
        </WinnerName>
      </HoverInfoTooltip>
    </WinnerHighlight>
  );
};

type Props = {
  vehicle: Vehicle;
};

export const AdminAAOffers = ({ vehicle }: Props) => {
  const offerAuction = useAdminOfferAuction(vehicle.id);
  const { highestBidOffer } = useOfferAuctionBidsInfo(offerAuction);
  const proxyOfferBid = offerAuction?.offerProxyBid;
  const hasOfferBids = (offerAuction?.offerBids?.length ?? 0) > 0;
  const isAwarded = vehicle.status === VEHICLE_STATUSES.AWARDED;
  const awardInfo = isAwarded && hasOfferBids && vehicle.awarded_user
    ? { amount: vehicle.vehicle_price ?? 0, buyerName: vehicle.awarded_user.dealership_name, userId: vehicle.awarded_user.id, auctionId: vehicle.auction_id }
    : undefined;

  return (
    <AdminOffers
      offers={offerAuction?.offerBids}
      highOffer={highestBidOffer || undefined}
      proxyOffer={proxyOfferBid}
      vehicleId={vehicle.id}
      type="offers"
      awardInfo={awardInfo}
      visibleBuyers={vehicle.adminVisibleBuyers ?? []}
    />
  );
};

export const AdminAlternativeOffers = ({ vehicle }: Props) => {
  const offers = vehicle.alternative_bid_offers;
  const highOffer = vehicle.top_alternative_bid_offer;
  const showMinBid = vehicle.auction && vehicle.auction.type === AUCTION_TYPES.SECOND_CHANCE;
  const isAwarded = vehicle.status === VEHICLE_STATUSES.AWARDED;
  const awardInfo = isAwarded && vehicle.awarded_user
    ? { amount: vehicle.vehicle_price ?? 0, buyerName: vehicle.awarded_user.dealership_name, userId: vehicle.awarded_user.id, auctionId: vehicle.auction_id }
    : undefined;

  return (
    <AdminOffers
      showMinBid={showMinBid}
      minBid={vehicle.starting_bid}
      offers={offers}
      highOffer={highOffer}
      proxyOffer={highOffer}
      vehicleId={vehicle.id}
      type="alternativeOffers"
      awardInfo={awardInfo}
      visibleBuyers={vehicle.adminVisibleBuyers ?? []}
    />
  );
};

type SerializedOffer = {
  id: number;
  amount: number;
  bidder: User;
  created_at: string;
};

type GeneralProps = {
  offers?: SerializedOffer[];
  highOffer?: SerializedOffer;
  proxyOffer?: SerializedOffer;
  showMinBid?: boolean;
  minBid?: number;
  vehicleId: number;
  type: 'offers' | 'alternativeOffers';
  awardInfo?: { amount: number; buyerName: string; userId?: number; auctionId?: number };
  visibleBuyers?: any[];
};

export const AdminOffers = ({ vehicleId, offers, highOffer, proxyOffer, type, showMinBid, minBid, awardInfo, visibleBuyers }: GeneralProps) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenPopover = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const vehicleOfferBids = (offers || []).map(bid => ({
    id: bid.id,
    bid: bid.amount,
    bidder: bid.bidder?.dealership_name,
    date: convertToLocalTime(bid.created_at, true),
    created_at: bid.created_at,
    vehicle_id: vehicleId
  }));

  return (
    <Wrapper>
      {showMinBid && (
        <span style={{ color: '#666666', fontSize: '12px' }}>
          <NumberFormat displayType="text" prefix="$" value={minBid || 0} thousandSeparator />
        </span>
      )}
      {awardInfo ? (
        <>
          <WinnerHighlight>
            <NumberFormat displayType="text" prefix="$" value={awardInfo.amount || 0} thousandSeparator />
          </WinnerHighlight>
          {awardInfo.userId && awardInfo.auctionId ? (
            <WinnerBuyerName buyerName={awardInfo.buyerName} userId={awardInfo.userId} auctionId={awardInfo.auctionId} />
          ) : (
            <WinnerHighlight>{awardInfo.buyerName}</WinnerHighlight>
          )}
        </>
      ) : (
        <span style={{ fontWeight: 500 }}>
          <NumberFormat displayType="text" prefix="$" value={highOffer?.amount ?? 0} thousandSeparator />
        </span>
      )}

      {vehicleOfferBids.length > 0 && (
        <>
          <StyledBackButton onClick={handleOpenPopover}>
            <Icon>expand_more</Icon>
          </StyledBackButton>
          <Popover
            id="offer-history-popover"
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            marginThreshold={150}
          >
            <ViewAllBidsTooltip
              bids={vehicleOfferBids}
              vehicleId={vehicleId}
              close={handleClosePopover}
              type={type}
              editable
              proxyBid={proxyOffer}
              visibleBuyers={visibleBuyers ?? []}
            />
          </Popover>
        </>
      )}
    </Wrapper>
  );
};
